// client/src/components/anamnese/AnamneseFillForm.js
// Renderizador dinâmico do formulário de anamnese — mesmo switch de 8 tipos
// que mobile/app/clientes/[id]/anamneses/nova.tsx já usa (Título, Texto
// Simples, Separador, Resposta em Texto, Verdadeiro ou Falso, Opções, Multi
// Opções, Galeria), com upload de foto real pro tipo Galeria (mobile só
// mostra aviso "preencha pelo painel web" pra esse tipo).
import React, { useRef, useState } from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { useUploadGaleriaAnamnese } from '../../hooks/useAnamneses';
import useAlert from '../../hooks/useAlert';

const TIPOS_COM_RESPOSTA = new Set(['Resposta em Texto', 'Verdadeiro ou Falso', 'Opções', 'Multi Opções', 'Galeria']);

const GaleriaField = ({ item, anamneseId, disabled }) => {
    const { setAlert } = useAlert();
    const uploadGaleria = useUploadGaleriaAnamnese();
    const fileInputRef = useRef(null);
    const [arquivos, setArquivos] = useState([]);

    const handleSelectFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        uploadGaleria.mutate(
            { id: anamneseId, itemId: item.id, file },
            {
                onSuccess: (arquivo) => {
                    setArquivos((prev) => [...prev, arquivo]);
                    setAlert('Foto adicionada.', 'success');
                },
            }
        );
    };

    const removerDaVisualizacao = (arquivoId) => {
        setArquivos((prev) => prev.filter((a) => a._id !== arquivoId));
    };

    return (
        <div className="form-group full-width">
            <label className="form-label">{item.label}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
                {arquivos.map((a) => (
                    <div key={a._id} style={{ position: 'relative', width: 96, height: 96 }}>
                        <img
                            src={a.url}
                            alt={item.label}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, border: '1px solid var(--color-border, #e5e7eb)' }}
                        />
                        <button
                            type="button"
                            onClick={() => removerDaVisualizacao(a._id)}
                            title="Remover da visualização (a foto já enviada permanece na ficha)"
                            style={{
                                position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%',
                                background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <X size={14} />
                        </button>
                    </div>
                ))}

                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || uploadGaleria.isPending}
                    style={{
                        width: 96, height: 96, borderRadius: 8, border: '1px dashed var(--color-border, #ccc)',
                        background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: 4, cursor: disabled ? 'default' : 'pointer', color: '#64748b',
                    }}
                >
                    {uploadGaleria.isPending ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
                    <span style={{ fontSize: '0.7rem' }}>Adicionar</span>
                </button>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSelectFile} />
        </div>
    );
};

const AnamneseFillForm = ({ template, respostas, onChangeResposta, anamneseId, disabled = false }) => {
    if (!template) return null;

    return (
        <div className="form-section">
            {(template.conteudo || []).map((item) => {
                switch (item.type) {
                    case 'Título':
                        return <h4 key={item.id} className="form-section-title">{item.label}</h4>;

                    case 'Texto Simples':
                        return <p key={item.id} className="text-muted" style={{ marginTop: '-0.5rem' }}>{item.label}</p>;

                    case 'Separador':
                        return <hr key={item.id} style={{ border: 'none', borderTop: '1px solid var(--color-border, #e5e7eb)', margin: '0.5rem 0' }} />;

                    case 'Resposta em Texto':
                        return (
                            <div key={item.id} className="form-group full-width">
                                <label className="form-label">{item.label}</label>
                                <textarea
                                    className="form-input"
                                    rows={3}
                                    disabled={disabled}
                                    value={respostas[item.id] || ''}
                                    onChange={(e) => onChangeResposta(item.id, e.target.value)}
                                />
                            </div>
                        );

                    case 'Verdadeiro ou Falso':
                        return (
                            <div key={item.id} className="form-group full-width">
                                <label className="form-label">{item.label}</label>
                                <div style={{ display: 'flex', gap: '1.5rem' }}>
                                    {[{ label: 'Sim', v: true }, { label: 'Não', v: false }].map((opt) => (
                                        <label key={opt.label} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: disabled ? 'default' : 'pointer' }}>
                                            <input
                                                type="radio"
                                                name={`item-${item.id}`}
                                                disabled={disabled}
                                                checked={respostas[item.id] === opt.v}
                                                onChange={() => onChangeResposta(item.id, opt.v)}
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        );

                    case 'Opções':
                        return (
                            <div key={item.id} className="form-group full-width">
                                <label className="form-label">{item.label}</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {(item.options || []).map((opt, i) => {
                                        // options é array de strings puras no formato salvo
                                        // (TemplateAnamneseForm.js achata {id,label}->label antes
                                        // de enviar pro backend) — opt.label é um fallback
                                        // defensivo, mesmo padrão de AnamnesePreview.js.
                                        const label = opt.label || opt;
                                        return (
                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'default' : 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name={`item-${item.id}`}
                                                    disabled={disabled}
                                                    checked={respostas[item.id] === label}
                                                    onChange={() => onChangeResposta(item.id, label)}
                                                />
                                                {label}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        );

                    case 'Multi Opções': {
                        const valorAtual = Array.isArray(respostas[item.id]) ? respostas[item.id] : [];
                        const toggle = (label) => {
                            onChangeResposta(item.id, valorAtual.includes(label) ? valorAtual.filter((v) => v !== label) : [...valorAtual, label]);
                        };
                        return (
                            <div key={item.id} className="form-group full-width">
                                <label className="form-label">{item.label}</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {(item.options || []).map((opt, i) => {
                                        const label = opt.label || opt;
                                        return (
                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'default' : 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    disabled={disabled}
                                                    checked={valorAtual.includes(label)}
                                                    onChange={() => toggle(label)}
                                                />
                                                {label}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    }

                    case 'Galeria':
                        return anamneseId ? (
                            <GaleriaField key={item.id} item={item} anamneseId={anamneseId} disabled={disabled} />
                        ) : null;

                    default:
                        return null;
                }
            })}
        </div>
    );
};

export { TIPOS_COM_RESPOSTA };
export default AnamneseFillForm;
